/**
 * useQuoteCalculations - Presentation Hook
 *
 * Provides derived calculations for quote display
 * Uses domain calculators for business logic
 *
 * @module useQuoteCalculations
 */

import { useMemo } from "react";
import {
  calculateQuoteAmounts,
  calculateQuoteSubtotal,
  formatCurrencyWithSymbol as formatCurrency,
  DEFAULT_VAT_MODE,
  type QuoteItem,
  type QuoteVatMode,
} from "@semo/shared-utils";

export interface QuoteCalculations {
  /** 품목 금액 합계 — 입력 축 그대로(부가세 포함 모드면 포함가 합계) */
  itemsTotal: number;
  /** 공급가액 */
  subtotal: number;
  /** 부가세 */
  vat: number;
  /** 부가세 포함 총액 */
  total: number;
  formattedSubtotal: string;
  formattedVAT: string;
  formattedTotal: string;
}

/**
 * Custom hook for quote price calculations
 *
 * 세율은 인자로 받지 않는다 — 호출부마다 세율을 넘기면 화면과 전송 payload 가 서로 다른
 * 값을 쓰기 쉽다. 규칙은 `calculateQuoteAmounts` 한 곳에 있다.
 *
 * @param items - Quote items to calculate totals for
 * @param mode - 단가 입력 축 (부가세 별도 / 부가세 포함)
 * @returns Calculated values and formatted strings
 */
export const useQuoteCalculations = (
  items: QuoteItem[],
  mode: QuoteVatMode = DEFAULT_VAT_MODE,
): QuoteCalculations => {
  const itemsTotal = useMemo(() => calculateQuoteSubtotal(items), [items]);

  const amounts = useMemo(
    () => calculateQuoteAmounts(items, mode),
    [items, mode],
  );

  const formattedSubtotal = useMemo(
    () => formatCurrency(amounts.supplyAmount),
    [amounts.supplyAmount],
  );

  const formattedVAT = useMemo(
    () => formatCurrency(amounts.vatAmount),
    [amounts.vatAmount],
  );

  const formattedTotal = useMemo(
    () => formatCurrency(amounts.totalAmount),
    [amounts.totalAmount],
  );

  return {
    itemsTotal,
    subtotal: amounts.supplyAmount,
    vat: amounts.vatAmount,
    total: amounts.totalAmount,
    formattedSubtotal,
    formattedVAT,
    formattedTotal,
  };
};
